import { useThree } from '@react-three/fiber';
import { useEffect } from 'react';
import { Box2, Shape, Vector2 } from 'three';
import { normalizePoint } from '@/components/property-selector/utils';
import { type Point } from '@/types';

export function FitBounds({
  points,
  padding = 0.33,
  duration = 1000,
}: {
  points?: Point[];
  padding?: number;
  duration?: number;
}) {
  const { size, camera } = useThree();

  useEffect(() => {
    let targetZoom = 1;
    let targetPosition = new Vector2(0, 0);
    if (points) {
      const shape = new Shape(
        points.map((p) => {
          const normalizedPoint = normalizePoint(p, size);
          return new Vector2(normalizedPoint.x, normalizedPoint.y);
        }),
      );
      // Calculate the bounding box
      const box2 = new Box2().setFromPoints(shape.getPoints());
      const boxSize = new Vector2();
      box2.getSize(boxSize); // Width (x) and height (y) of the bounding box

      const boxCenter = new Vector2();
      box2.getCenter(boxCenter); // Center of the bounding box

      // animate to position and zoom
      const center = new Vector2();
      box2.getCenter(center);

      targetZoom = Math.min(size.height / boxSize.x, size.height / boxSize.y) / (1 + padding);
      targetPosition = new Vector2(center.x, center.y);
    }
    const startZoom = camera.zoom;
    const startPosition = new Vector2(camera.position.x, camera.position.y);
    const startTime = Date.now();

    let request: number;
    // add easy-in-out animation
    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const ease = 0.5 - 0.5 * Math.cos(progress * Math.PI);
      camera.zoom = startZoom + (targetZoom - startZoom) * ease;
      camera.position.set(
        startPosition.x + (targetPosition.x - startPosition.x) * ease,
        startPosition.y + (targetPosition.y - startPosition.y) * ease,
        camera.position.z,
      );
      camera.updateProjectionMatrix();
      if (progress < 1) {
        request = requestAnimationFrame(animate);
      }
    };
    const timeout = setTimeout(animate, 300);

    return () => {
      cancelAnimationFrame(request);
      clearTimeout(timeout);
    };
  }, [camera, duration, padding, points, size]);

  return null;
}
